/** @format */

import { detectChartTypeFromQuestion, wantsChartFromQuestion } from './buildAiChatEChartsOption';

const HIDDEN_TABLE_COLUMNS = new Set([
  'id',
  'related_object',
  'related_record_id',
  'currency',
  'exchange_rate',
]);

/**
 * Detect nested include keys on relationship-query rows.
 * @param {Array<object>|undefined} rows
 * @param {string[]|undefined} includeKeys
 */
export function detectNestedIncludeKeys(rows, includeKeys) {
  if (Array.isArray(includeKeys) && includeKeys.length > 0) {
    return includeKeys.filter(key => key && typeof key === 'string');
  }
  if (!Array.isArray(rows) || !rows.length) return [];
  const first = rows[0];
  if (!first || typeof first !== 'object') return [];
  return Object.keys(first).filter(key => Array.isArray(first[key]));
}

/**
 * Columns for the parent table (exclude nested include arrays).
 * @param {object} [options]
 * @param {string} [options.dataFormat]
 * @param {string[]} [options.groupBy]
 */
export function resolveQueryTableColumns(rows, displayColumns, includeKeys, options = {}) {
  const nestedKeys = new Set(detectNestedIncludeKeys(rows, includeKeys));
  const dataFormat = options?.dataFormat;
  const isAggregate =
    dataFormat === 'aggregate' || dataFormat === 'currencyAggregate';
  const groupBySet = new Set(
    Array.isArray(options?.groupBy) ? options.groupBy : []
  );

  const hideOwnerIdColumn = (col) => {
    const lower = String(col).toLowerCase();
    if (lower !== 'ownerid' && lower !== 'owner_id') return false;
    // Aggregate group-by dimensions must stay visible (e.g. count by owner).
    if (isAggregate && (groupBySet.has(col) || displayColumns?.includes(col))) {
      return false;
    }
    return true;
  };

  if (Array.isArray(displayColumns) && displayColumns.length > 0) {
    return displayColumns.filter(col => {
      if (HIDDEN_TABLE_COLUMNS.has(col) || nestedKeys.has(col)) return false;
      if (hideOwnerIdColumn(col)) return false;
      return true;
    });
  }

  if (!Array.isArray(rows) || !rows.length) return [];
  const first = rows[0];
  if (!first || typeof first !== 'object') return [];

  return Object.keys(first).filter(col => {
    if (HIDDEN_TABLE_COLUMNS.has(col) || nestedKeys.has(col)) return false;
    if (Array.isArray(first[col])) return false;
    if (hideOwnerIdColumn(col)) return false;
    return true;
  });
}

/**
 * Safe filename fragment from a result title.
 */
export function sanitizeResultTitleForFilename(title) {
  const base = String(title || 'Query_Results')
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '_')
    .slice(0, 60);
  return base || 'Query_Results';
}

/**
 * Whether charts make sense for this response shape.
 */
export function shouldShowChartForQueryResponse(data, question) {
  if (!wantsChartFromQuestion(question)) return false;
  if (
    data?.queryType === 'sql' ||
    data?.queryType === 'relationship_aggregate' ||
    data?.dataFormat === 'aggregate' ||
    data?.dataFormat === 'currencyAggregate'
  ) {
    return true;
  }
  const includeKeys = detectNestedIncludeKeys(data?.data, data?.includeKeys);
  if (includeKeys.length > 0) return false;
  return Array.isArray(data?.data) && data.data.length > 0;
}

/**
 * Map /ai-query/execute API payload → assistant message fields.
 */
export function mapQueryApiResponseToMessage(data, question) {
  if (!data?.success) {
    return {
      role: 'assistant',
      error: data?.message || 'No data returned.',
    };
  }

  const wantsChart = wantsChartFromQuestion(question);
  const chartType = wantsChart ? detectChartTypeFromQuestion(question) : 'bar';
  const includeKeys = detectNestedIncludeKeys(data?.data, data?.includeKeys);
  const showChart = shouldShowChartForQueryResponse(data, question);

  const resultTitle =
    (typeof data?.resultTitle === 'string' && data.resultTitle.trim()) ||
    (typeof data?.message === 'string' &&
    data.message.trim() &&
    data.message.trim() !== 'Here are your results.'
      ? data.message.trim()
      : '') ||
    (typeof question === 'string' && question.trim()
      ? question.trim().charAt(0).toUpperCase() + question.trim().slice(1).slice(0, 160)
      : '') ||
    undefined;

  const base = {
    role: 'assistant',
    text: resultTitle || data?.message || 'Here are your results.',
    resultTitle,
    rowCount: data?.rowCount ?? (Array.isArray(data?.data) ? data.data.length : undefined),
    columnLabels: data?.columnLabels,
    columnMeta: data?.columnMeta,
    displayColumns: data?.displayColumns,
    dataFormat: data?.dataFormat,
    groupBy: Array.isArray(data?.groupBy) ? data.groupBy : undefined,
    objectName: data?.objectName,
    queryType: data?.queryType,
    executionPath: data?.executionPath,
    routingReason: data?.routingReason,
    includeKeys: includeKeys.length ? includeKeys : undefined,
    showChart,
    chartType,
  };

  if (data?.success && Array.isArray(data?.data)) {
    return { ...base, data: data.data };
  }
  if (data?.success && data?.data) {
    return {
      ...base,
      data: Array.isArray(data.data) ? data.data : [data.data],
    };
  }
  return {
    role: 'assistant',
    error: data?.message || 'No data returned.',
  };
}

/**
 * Format a nested include array cell (e.g. contacts: [...]).
 */
export function formatIncludeCellSummary(value, columnLabel) {
  if (!Array.isArray(value)) return '';
  const label = String(columnLabel || 'records').trim();
  const singular = label.replace(/s$/i, '') || 'record';
  if (value.length === 0) return `No ${label.toLowerCase()}`;
  if (value.length === 1) return `1 ${singular}`;
  return `${value.length} ${label}`;
}
